import { useEffect, useMemo, useState, useCallback } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { DateRangePicker } from "@/components/ui/date-range-picker";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { RefreshCw, User, LogIn, LogOut, Plus, Pencil, Trash2, Filter, X, Eye, KeyRound } from "lucide-react";
import CommonGrid from "@/components/pos/admin/common/grid";
import NoRecordFound from "@/components/pos/admin/common/no-record-found";
import { usePagePersistentState } from "@/hooks/pos/use-page-persisted-state";
import { useDateFormatter } from "@/hooks/pos/use-date-formatter";
import { useDebounce } from "@/hooks/pos/use-debounce";
import { usePosAuth } from "@/hooks/pos/use-pos-auth";
import { posApiFetch } from "@/utils/pos-api";
import { useToast } from "@/hooks/use-toast";
import { type DateRange } from "react-day-picker";
import { format, subDays } from "date-fns";

type ActivityLogsState = {
  dateRange?: { from: string; to: string };
  actionFilter?: string;
  entityTypeFilter?: string;
  userFilter?: string;
};

const defaultActivityLogsState: ActivityLogsState = {
  actionFilter: "",
  entityTypeFilter: "",
  userFilter: "",
};

const ACTION_OPTIONS = [
  { label: "All", value: "all" },
  { label: "Login", value: "login" },
  { label: "Logout", value: "logout" },
  { label: "Create", value: "create" },
  { label: "Update", value: "update" },
  { label: "Delete", value: "delete" },
  { label: "Password Reset Request", value: "password_reset_request" },
  { label: "Password Reset Complete", value: "password_reset_complete" },
];

const ENTITY_TYPE_OPTIONS = [
  { label: "All", value: "all" },
  { label: "Users", value: "users" },
  { label: "Products", value: "products" },
  { label: "Locations", value: "locations" },
  { label: "Menu Types", value: "menu_types" },
  { label: "Subcategories", value: "subcategories" },
  { label: "Custom Drinks", value: "custom_drinks" },
  { label: "Modifier Groups", value: "modifier_groups" },
  { label: "Modifiers", value: "modifiers" },
];

const getActionBadgeColor = (action: string): string => {
  const lower = action?.toLowerCase();
  if (lower === "login") return "bg-green-100 text-green-800";
  if (lower === "logout") return "bg-gray-100 text-gray-800";
  if (lower === "create") return "bg-blue-100 text-blue-800";
  if (lower === "update") return "bg-yellow-100 text-yellow-800";
  if (lower === "delete") return "bg-red-100 text-red-800";
  if (lower?.startsWith("password_reset")) return "bg-purple-100 text-purple-800";
  return "bg-gray-100 text-gray-800";
};

const getActionIcon = (action: string) => {
  const lower = action?.toLowerCase();
  if (lower === "login") return LogIn;
  if (lower === "logout") return LogOut;
  if (lower === "create") return Plus;
  if (lower === "update") return Pencil;
  if (lower === "delete") return Trash2;
  if (lower?.startsWith("password_reset")) return KeyRound;
  return User;
};

const fetchActivityLogs = async (params: {
  startDate?: string;
  endDate?: string;
  action?: string;
  entityType?: string;
  userId?: string;
  limit: number;
  offset: number;
}) => {
  const searchParams = new URLSearchParams();
  if (params.startDate) searchParams.append("startDate", params.startDate);
  if (params.endDate) searchParams.append("endDate", params.endDate);
  if (params.action && params.action !== "all") searchParams.append("action", params.action);
  if (params.entityType && params.entityType !== "all") searchParams.append("entityType", params.entityType);
  if (params.userId && params.userId !== "all") searchParams.append("userId", params.userId);
  searchParams.append("limit", String(params.limit));
  searchParams.append("offset", String(params.offset));

  const qs = searchParams.toString();
  const url = `/v1/pos/activity-logs${qs ? `?${qs}` : ""}`;
  const res = await posApiFetch(url);
  if (!res.ok) throw new Error("Failed to fetch activity logs");
  return res.json();
};

const fetchPosUsers = async () => {
  const res = await posApiFetch("/v1/pos/users");
  if (!res.ok) return [];
  const data = await res.json();
  return Array.isArray(data) ? data : Array.isArray(data?.users) ? data.users : Array.isArray(data?.data) ? data.data : [];
};

export default function ActivityLogsPage() {
  const { toast } = useToast();
  const { formatDate } = useDateFormatter();
  const { hasRole } = usePosAuth();
  const queryClient = useQueryClient();

  useEffect(() => {
    document.title = "Activity Logs | SECH Admin";
  }, []);

  const isAdmin = hasRole("admin");

  const [logsState, setLogsState] = usePagePersistentState<ActivityLogsState>("activity-logs", defaultActivityLogsState);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [detailModal, setDetailModal] = useState(false);
  const [selectedLog, setSelectedLog] = useState<any>(null);

  const actionFilter = logsState.actionFilter ?? "";
  const entityTypeFilter = logsState.entityTypeFilter ?? "";
  const userFilter = logsState.userFilter ?? "";

  const { data: posUsers } = useQuery({
    queryKey: ["/v1/pos/users"],
    queryFn: fetchPosUsers,
    retry: 1,
    enabled: isAdmin,
    staleTime: 5 * 60 * 1000,
  });

  const userOptions = useMemo(() => {
    const opts = [{ label: "All Users", value: "all" }];
    if (Array.isArray(posUsers)) {
      for (const u of posUsers) {
        opts.push({ label: u.displayName || u.email, value: String(u.id) });
      }
    }
    return opts;
  }, [posUsers]);

  const [dateRange, setDateRange] = useState<DateRange | undefined>(() => {
    if (logsState.dateRange?.from && logsState.dateRange?.to) {
      const from = new Date(logsState.dateRange.from);
      const to = new Date(logsState.dateRange.to);
      if (!isNaN(from.getTime()) && !isNaN(to.getTime())) return { from, to };
    }
    const defaultFrom = subDays(new Date(), 7);
    const defaultTo = new Date();
    return { from: defaultFrom, to: defaultTo };
  });

  const startDate = dateRange?.from ? format(dateRange.from, "yyyy-MM-dd") + "T00:00:00" : undefined;
  const endDate = dateRange?.to ? format(dateRange.to, "yyyy-MM-dd") + "T23:59:59" : undefined;
  const offset = (currentPage - 1) * itemsPerPage;

  const { data, isLoading, error } = useQuery({
    queryKey: ["/v1/pos/activity-logs", startDate, endDate, actionFilter, entityTypeFilter, userFilter, itemsPerPage, offset],
    queryFn: () => fetchActivityLogs({
      startDate,
      endDate,
      action: actionFilter,
      entityType: entityTypeFilter,
      userId: userFilter,
      limit: itemsPerPage,
      offset,
    }),
    retry: 2,
    enabled: isAdmin,
  });

  const logs = useMemo(() => {
    if (!data) return [];
    return Array.isArray(data) ? data : Array.isArray(data?.logs) ? data.logs : Array.isArray(data?.data) ? data.data : [];
  }, [data]);

  const totalItems = useMemo(() => {
    if (!data) return 0;
    return data?.total ?? data?.totalItems ?? data?.count ?? logs.length;
  }, [data, logs]);

  const totalPages = Math.ceil(totalItems / itemsPerPage);

  const handleActionFilterChange = (value: string) => {
    setLogsState((prev) => ({ ...prev, actionFilter: value }));
    setCurrentPage(1);
  };

  const handleEntityTypeFilterChange = (value: string) => {
    setLogsState((prev) => ({ ...prev, entityTypeFilter: value }));
    setCurrentPage(1);
  };

  const handleUserFilterChange = (value: string) => {
    setLogsState((prev) => ({ ...prev, userFilter: value }));
    setCurrentPage(1);
  };

  const handleDateRangeChange = (range: DateRange | undefined) => {
    setDateRange(range);
    setCurrentPage(1);
    if (range?.from && range?.to) {
      setLogsState((prev) => ({
        ...prev,
        dateRange: { from: format(range.from!, "yyyy-MM-dd"), to: format(range.to!, "yyyy-MM-dd") },
      }));
    } else {
      setLogsState((prev) => ({ ...prev, dateRange: { from: "", to: "" } }));
    }
  };

  const handleRefresh = () => {
    queryClient.invalidateQueries({ queryKey: ["/v1/pos/activity-logs"] });
    toast({ title: "Activity logs refreshed" });
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleItemsPerPageChange = (value: string) => {
    setItemsPerPage(Number(value));
    setCurrentPage(1);
  };

  const handleViewDetails = useCallback((log: any) => {
    setSelectedLog(log);
    setDetailModal(true);
  }, []);

  const tableColumns = useMemo(() => [
    {
      label: "Time", field: "createdAt", sortable: false,
      cellTemplate: (value: string) => formatDate(value),
    },
    {
      label: "User", field: "userDisplayName", sortable: false,
      cellTemplate: (value: string, row: any) => (
        <span className="text-[13px] text-[var(--secondary-text-color)]">
          {value || row?.userEmail || "System"}
        </span>
      ),
    },
    {
      label: "Action", field: "action", sortable: false,
      cellTemplate: (value: string) => {
        const Icon = getActionIcon(value);
        const badgeColor = getActionBadgeColor(value);
        const displayAction = value ? value.replace(/_/g, " ").replace(/\b\w/g, (c: string) => c.toUpperCase()) : "-";
        return (
          <span className={`px-2 py-0.5 inline-flex items-center gap-1 rounded-full text-xs font-medium ${badgeColor}`}>
            <Icon className="h-3 w-3" />
            {displayAction}
          </span>
        );
      },
    },
    {
      label: "Entity Type", field: "entityType", sortable: false,
      cellTemplate: (value: string) => (
        <span className="text-[13px] text-[var(--secondary-text-color)]">
          {value ? value.replace(/_/g, " ").replace(/\b\w/g, (c: string) => c.toUpperCase()) : "-"}
        </span>
      ),
    },
    {
      label: "Entity Name", field: "entityName", sortable: false,
      cellTemplate: (value: string) => (
        <span className="text-[13px] text-[var(--secondary-text-color)]">{value || "-"}</span>
      ),
    },
    {
      label: "Details", field: "details", sortable: false,
      cellTemplate: (_value: any, row: any) => (
        <button
          type="button"
          onClick={() => handleViewDetails(row)}
          className="text-[13px] text-[var(--primary-color)] hover:text-[#CC1417] flex items-center gap-1 cursor-pointer"
        >
          <Eye className="h-3 w-3" />
          View Details
        </button>
      ),
    },
    {
      label: "IP Address", field: "ipAddress", sortable: false,
      cellTemplate: (value: string) => (
        <span className="text-[13px] text-[var(--secondary-text-color)]">{value || "-"}</span>
      ),
    },
  ], [handleViewDetails]);

  const pagination = {
    totalPages,
    currentPage,
    itemsPerPage,
    totalItems,
    handlePageChange,
    handleItemsPerPageChange,
  };

  if (!isAdmin) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Card className="max-w-md">
          <CardContent className="p-6 text-center">
            <h2 className="text-xl font-bold text-red-600 mb-4">Access Denied</h2>
            <p className="text-gray-600">You do not have permission to view activity logs. Admin access is required.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Card className="max-w-md">
          <CardContent className="p-6">
            <h2 className="text-xl font-bold text-red-600 mb-4">Error Loading Activity Logs</h2>
            <p className="text-gray-600 mb-4">Failed to load activity logs</p>
            <Button onClick={() => window.location.reload()} className="w-full">Reload Page</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="h-full overflow-y-auto">
      <div className="page-header flex justify-between items-center h-[48px] px-[16px] border-b border-[var(--layout-border-color)]">
        <p className="page-heading text-[20px] leading-[30px] font-semibold">Activity Logs</p>
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-1">
            <Filter className="h-4 w-4 text-[var(--secondary-text-color)]" />
          </div>
          <Select value={actionFilter || "all"} onValueChange={handleActionFilterChange}>
            <SelectTrigger className="h-[32px] w-[180px] rounded-[4px] border border-[var(--ui-border-color)] px-2 text-[13px] text-[var(--secondary-text-color)]">
              <SelectValue placeholder="Action" />
            </SelectTrigger>
            <SelectContent>
              <SelectGroup>
                {ACTION_OPTIONS.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                ))}
              </SelectGroup>
            </SelectContent>
          </Select>

          <Select value={entityTypeFilter || "all"} onValueChange={handleEntityTypeFilterChange}>
            <SelectTrigger className="h-[32px] w-[180px] rounded-[4px] border border-[var(--ui-border-color)] px-2 text-[13px] text-[var(--secondary-text-color)]">
              <SelectValue placeholder="Entity Type" />
            </SelectTrigger>
            <SelectContent>
              <SelectGroup>
                {ENTITY_TYPE_OPTIONS.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                ))}
              </SelectGroup>
            </SelectContent>
          </Select>

          <Select value={userFilter || "all"} onValueChange={handleUserFilterChange}>
            <SelectTrigger className="h-[32px] w-[180px] rounded-[4px] border border-[var(--ui-border-color)] px-2 text-[13px] text-[var(--secondary-text-color)]">
              <SelectValue placeholder="User" />
            </SelectTrigger>
            <SelectContent>
              <SelectGroup>
                {userOptions.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                ))}
              </SelectGroup>
            </SelectContent>
          </Select>

          <DateRangePicker value={dateRange} onChange={handleDateRangeChange} placeholder="Date Range" />

          {(actionFilter && actionFilter !== "all") || (entityTypeFilter && entityTypeFilter !== "all") || (userFilter && userFilter !== "all") ? (
            <Button
              variant="ghost"
              onClick={() => {
                setLogsState((prev) => ({ ...prev, actionFilter: "", entityTypeFilter: "", userFilter: "" }));
                setCurrentPage(1);
              }}
              className="h-[32px] px-2 text-[13px]"
            >
              <X className="h-4 w-4 mr-1" /> Clear
            </Button>
          ) : null}

          <Button variant="outline" onClick={handleRefresh} className="h-[32px] w-[32px] p-0 rounded-[4px]">
            <RefreshCw className="h-4 w-4" />
          </Button>
        </div>
      </div>

      <div className="p-[16px]">
        <div className="h-[calc(100vh-130px)]">
          {!logs.length && !isLoading ? (
            <NoRecordFound title="Activity Logs" description="No activity logs found for the selected filters." />
          ) : (
            <CommonGrid
              rowData={logs}
              isLoading={isLoading}
              columns={tableColumns}
              pagination={pagination}
              isPaginationRequired={true}
            />
          )}
        </div>
      </div>

      <Dialog open={detailModal} onOpenChange={(open) => { if (!open) { setDetailModal(false); setSelectedLog(null); } }}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Activity Log Details</DialogTitle>
          </DialogHeader>
          {selectedLog && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-[12px] font-medium text-[var(--secondary-text-color)]">Time</p>
                  <p className="text-[13px] text-[var(--primary-text-color)]">{selectedLog.createdAt ? new Date(selectedLog.createdAt).toLocaleString() : "-"}</p>
                </div>
                <div>
                  <p className="text-[12px] font-medium text-[var(--secondary-text-color)]">User</p>
                  <p className="text-[13px] text-[var(--primary-text-color)]">{selectedLog.userDisplayName || selectedLog.userEmail || "System"}</p>
                </div>
                <div>
                  <p className="text-[12px] font-medium text-[var(--secondary-text-color)]">Action</p>
                  <p className="text-[13px] text-[var(--primary-text-color)]">{selectedLog.action ? selectedLog.action.replace(/_/g, " ").replace(/\b\w/g, (c: string) => c.toUpperCase()) : "-"}</p>
                </div>
                <div>
                  <p className="text-[12px] font-medium text-[var(--secondary-text-color)]">Entity Type</p>
                  <p className="text-[13px] text-[var(--primary-text-color)]">{selectedLog.entityType ? selectedLog.entityType.replace(/_/g, " ").replace(/\b\w/g, (c: string) => c.toUpperCase()) : "-"}</p>
                </div>
                <div>
                  <p className="text-[12px] font-medium text-[var(--secondary-text-color)]">Entity Name</p>
                  <p className="text-[13px] text-[var(--primary-text-color)]">{selectedLog.entityName || "-"}</p>
                </div>
                <div>
                  <p className="text-[12px] font-medium text-[var(--secondary-text-color)]">IP Address</p>
                  <p className="text-[13px] text-[var(--primary-text-color)]">{selectedLog.ipAddress || "-"}</p>
                </div>
              </div>
              <div>
                <p className="text-[12px] font-medium text-[var(--secondary-text-color)] mb-2">Full Log Data</p>
                <pre className="bg-[var(--secondary-background-color)] p-4 rounded-[4px] text-[12px] text-[var(--primary-text-color)] overflow-x-auto whitespace-pre-wrap break-all">
                  {JSON.stringify(selectedLog, null, 2)}
                </pre>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
